import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Save, Pencil, Eye, Tags, Sparkles } from "lucide-react";
import { FilmActivitiesDialog } from "@/components/admin/FilmActivitiesDialog";
import { FilmPreviewDialog, type FilmPreviewData } from "@/components/festival/FilmPreviewDialog";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { parseYouTubeId } from "@/lib/youtube";
import {
  PRESET_BADGES,
  canonicalPreset,
  getBadgeStyle,
  resolveBadgeStyle,
  findCustomBadge,
  type CustomBadge,
} from "@/lib/badges";
import {
  loadCustomBadges,
  saveCustomBadges,
  useCustomBadges,
} from "@/hooks/use-custom-badges";

function parseBadges(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function serializeBadges(badges: string[]): string {
  return badges.join(", ");
}

function BadgeEditor({
  value,
  onChange,
  customBadges,
}: {
  value: string;
  onChange: (next: string) => void;
  customBadges: CustomBadge[];
}) {
  const badges = parseBadges(value);
  const activePresets = new Set(
    badges.map((b) => canonicalPreset(b)).filter((b): b is (typeof PRESET_BADGES)[number] => b !== null),
  );
  const activeCustoms = new Set(
    badges
      .map((b) => findCustomBadge(b, customBadges)?.label ?? null)
      .filter((b): b is string => b !== null),
  );
  const freeBadges = badges.filter(
    (b) => canonicalPreset(b) === null && !findCustomBadge(b, customBadges),
  );
  const freeText = freeBadges.join(", ");

  const rebuild = (
    nextPresets: (typeof PRESET_BADGES)[number][],
    nextCustoms: string[],
    freeParsed: string[],
  ) => {
    const orderedPresets = PRESET_BADGES.filter((p) => nextPresets.includes(p));
    const orderedCustoms = customBadges
      .map((c) => c.label)
      .filter((l) => nextCustoms.includes(l));
    onChange(serializeBadges([...orderedPresets, ...orderedCustoms, ...freeParsed]));
  };

  const togglePreset = (preset: (typeof PRESET_BADGES)[number]) => {
    const nextPresets = activePresets.has(preset)
      ? [...activePresets].filter((p) => p !== preset)
      : [...activePresets, preset];
    rebuild(nextPresets, [...activeCustoms], parseBadges(freeText));
  };

  const toggleCustom = (label: string) => {
    const nextCustoms = activeCustoms.has(label)
      ? [...activeCustoms].filter((l) => l !== label)
      : [...activeCustoms, label];
    rebuild([...activePresets], nextCustoms, parseBadges(freeText));
  };

  const handleFreeChange = (text: string) => {
    const parsedFree = parseBadges(text).filter(
      (b) => canonicalPreset(b) === null && !findCustomBadge(b, customBadges),
    );
    rebuild([...activePresets], [...activeCustoms], parsedFree);
  };

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-2">
        {PRESET_BADGES.map((preset) => {
          const active = activePresets.has(preset);
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={active}
              onClick={() => togglePreset(preset)}
              className={`rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider transition-all ${
                active
                  ? getBadgeStyle(preset)
                  : "border border-border/60 text-foreground/55 hover:border-gold/40 hover:text-gold/80"
              }`}
            >
              {preset}
            </button>
          );
        })}
        {customBadges.map((c) => {
          const active = activeCustoms.has(c.label);
          const s = resolveBadgeStyle(c.label, customBadges);
          return (
            <button
              key={c.label}
              type="button"
              aria-pressed={active}
              onClick={() => toggleCustom(c.label)}
              className={`rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider transition-all ${
                active
                  ? s.className
                  : "border border-border/60 text-foreground/55 hover:border-foreground/40 hover:text-foreground/80"
              }`}
              style={active ? s.style : undefined}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <Input
        value={freeText}
        onChange={(e) => handleFreeChange(e.target.value)}
        placeholder="Vrije extra badges, komma-gescheiden (optioneel)"
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/films")({
  head: () => ({
    meta: [
      { title: "Admin — Films beheren" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminFilmsPage,
});

type FilmStatus = "draft" | "published" | "hidden";

type FilmRow = {
  id: string;
  title: string;
  play_date: string;
  festival_day: number | null;
  teaser: string | null;
  description: string | null;
  poster_url: string | null;
  start_time: string | null;
  duration_minutes: number | null;
  genre: string | null;
  age_rating: string | null;
  badges: string[];
  cta_label: string | null;
  cta_url: string | null;
  status: FilmStatus;
  show_on_homepage: boolean;
  sort_order: number;
  trailer_url: string | null;
  show_activities_badge: boolean;
  activities_badge_label: string | null;
};

const EMPTY_FORM: Omit<FilmRow, "id" | "festival_day"> = {
  title: "",
  play_date: "2026-08-14",
  teaser: "",
  description: "",
  poster_url: null,
  start_time: "21:00",
  duration_minutes: null,
  genre: "",
  age_rating: "AL",
  badges: [],
  cta_label: "",
  cta_url: "",
  status: "draft",
  show_on_homepage: true,
  sort_order: 0,
  trailer_url: "",
  show_activities_badge: false,
  activities_badge_label: null,
};

const FESTIVAL_DATES = Array.from({ length: 9 }, (_, i) => {
  const d = new Date(2026, 7, 14 + i);
  const iso = `2026-08-${String(14 + i).padStart(2, "0")}`;
  const wk = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"][d.getDay()];
  return { iso, label: `${wk} ${14 + i} augustus`, day: i + 1 };
});

function PosterPreview({ pathOrUrl }: { pathOrUrl: string | null }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!pathOrUrl) {
      setSrc(null);
      return;
    }
    if (/^https?:\/\//i.test(pathOrUrl)) {
      setSrc(pathOrUrl);
      return;
    }
    supabase.storage
      .from("film-posters")
      .createSignedUrl(pathOrUrl, 60 * 60)
      .then(({ data }) => setSrc(data?.signedUrl ?? null));
  }, [pathOrUrl]);
  if (!src) return <div className="h-24 w-16 rounded bg-night-deep/60" />;
  return <img src={src} alt="poster" className="h-24 w-16 rounded object-cover" />;
}

function AdminFilmsPage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [films, setFilms] = useState<FilmRow[]>([]);
  const [sectionVisible, setSectionVisible] = useState<boolean>(true);
  const [ticketsEnabled, setTicketsEnabled] = useState<boolean>(false);
  const [ticketsUrl, setTicketsUrl] = useState<string>("");
  const [ticketsUrlDraft, setTicketsUrlDraft] = useState<string>("");
  const [editing, setEditing] = useState<FilmRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<FilmRow | null>(null);
  const [activitiesFor, setActivitiesFor] = useState<FilmRow | null>(null);
  const customBadges = useCustomBadges();

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin");
      setIsAdmin(!!roles && roles.length > 0);
    })();
  }, []);

  async function loadFilms() {
    const { data, error } = await (supabase as any)
      .from("festival_films")
      .select("*")
      .order("play_date", { ascending: true })
      .order("sort_order", { ascending: true });
    if (!error && data) setFilms(data as FilmRow[]);
  }

  async function loadSetting() {
    const { data } = await (supabase as any)
      .from("site_settings")
      .select("value")
      .eq("key", "homepage_program_section_visible")
      .maybeSingle();
    setSectionVisible(data?.value === true || data?.value === "true");
  }

  async function loadTicketSettings() {
    const { data } = await (supabase as any)
      .from("site_settings")
      .select("key,value")
      .in("key", ["tickets_enabled", "tickets_url"]);
    let enabled = false;
    let url = "";
    for (const row of (data ?? []) as Array<{ key: string; value: unknown }>) {
      if (row.key === "tickets_enabled") enabled = row.value === true || row.value === "true";
      if (row.key === "tickets_url" && typeof row.value === "string") url = row.value;
    }
    setTicketsEnabled(enabled);
    setTicketsUrl(url);
    setTicketsUrlDraft(url);
  }

  useEffect(() => {
    if (isAdmin) {
      loadFilms();
      loadSetting();
      loadTicketSettings();
    }
  }, [isAdmin]);

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }

  async function toggleSection(next: boolean) {
    setSectionVisible(next);
    const { error } = await (supabase as any)
      .from("site_settings")
      .upsert({ key: "homepage_program_section_visible", value: next });
    if (error) {
      toast.error(error.message);
      loadSetting();
    } else {
      toast.success(next ? "Sectie zichtbaar op homepage" : "Sectie verborgen op homepage");
    }
  }

  async function toggleTicketsEnabled(next: boolean) {
    setTicketsEnabled(next);
    const { error } = await (supabase as any)
      .from("site_settings")
      .upsert({ key: "tickets_enabled", value: next });
    if (error) {
      toast.error(error.message);
      loadTicketSettings();
    } else {
      toast.success(next ? "Ticketknoppen zichtbaar" : "Ticketknoppen verborgen");
    }
  }

  async function saveTicketsUrl() {
    const trimmed = ticketsUrlDraft.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      toast.error("URL moet met http:// of https:// beginnen");
      return;
    }
    const { error } = await (supabase as any)
      .from("site_settings")
      .upsert({ key: "tickets_url", value: trimmed });
    if (error) {
      toast.error(error.message);
      return;
    }
    setTicketsUrl(trimmed);
    toast.success("Ticket-URL opgeslagen");
  }

  async function updateFilm(id: string, patch: Partial<FilmRow>) {
    setFilms((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
    const { error } = await (supabase as any).from("festival_films").update(patch).eq("id", id);
    if (error) {
      toast.error(error.message);
      loadFilms();
    }
  }

  async function deleteFilm(id: string) {
    if (!confirm("Verwijder deze film?")) return;
    const { error } = await (supabase as any).from("festival_films").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Verwijderd");
    loadFilms();
  }

  const grouped = useMemo(() => {
    const map = new Map<string, FilmRow[]>();
    for (const f of films) {
      const arr = map.get(f.play_date) ?? [];
      arr.push(f);
      map.set(f.play_date, arr);
    }
    return map;
  }, [films]);

  if (isAdmin === null) {
    return <div className="flex min-h-screen items-center justify-center text-foreground/70">Laden…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5">
        <div className="max-w-md space-y-4 text-center">
          <h1 className="font-display text-2xl">Geen toegang</h1>
          <p className="text-foreground/70">Je account heeft geen admin rechten.</p>
          <Button onClick={handleSignOut} variant="ghostLine">Uitloggen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-5 py-12 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8">
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium md:text-4xl">Films beheren</h1>
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/30 bg-night-deep/40 p-5">
          <div>
            <p className="font-display text-lg">Programma-sectie op homepage</p>
            <p className="mt-1 text-sm text-foreground/70">
              Zet de volledige sectie op de homepage aan of uit. Losse films kun je
              per stuk verbergen of tonen.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground/70">{sectionVisible ? "Aan" : "Uit"}</span>
            <Switch checked={sectionVisible} onCheckedChange={toggleSection} />
          </div>
        </div>

        <div className="mb-8 space-y-4 rounded-2xl border border-gold/30 bg-night-deep/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-display text-lg">&ldquo;Koop tickets&rdquo;-knoppen</p>
              <p className="mt-1 text-sm text-foreground/70">
                Schakel alle ticketknoppen (navigatie, hero, sticky mobiel, CTA) tegelijk aan of uit.
                Vul optioneel een externe URL in; is die leeg dan blijven knoppen naar
                <code className="mx-1 rounded bg-night-deep/60 px-1">#tickets</code> springen.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground/70">{ticketsEnabled ? "Aan" : "Uit"}</span>
              <Switch checked={ticketsEnabled} onCheckedChange={toggleTicketsEnabled} />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px] flex-1">
              <Label htmlFor="tickets-url">Ticketshop URL</Label>
              <Input
                id="tickets-url"
                type="url"
                inputMode="url"
                placeholder="https://tickets.voorbeeld.nl/openluchtfilmfestival"
                value={ticketsUrlDraft}
                onChange={(e) => setTicketsUrlDraft(e.target.value)}
              />
            </div>
            <Button
              variant="hero"
              size="sm"
              onClick={saveTicketsUrl}
              disabled={ticketsUrlDraft.trim() === ticketsUrl}
            >
              <Save className="h-4 w-4" /> Opslaan
            </Button>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl">Films ({films.length})</h2>
          <Button variant="hero" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nieuwe film
          </Button>
        </div>

        <CustomBadgeManager badges={customBadges} />


        {films.length === 0 && (
          <p className="rounded-xl border border-dashed border-border p-8 text-center text-foreground/60">
            Nog geen films toegevoegd. Klik op &ldquo;Nieuwe film&rdquo; om te beginnen.
          </p>
        )}

        <div className="space-y-8">
          {FESTIVAL_DATES.map((d) => {
            const dayFilms = grouped.get(d.iso) ?? [];
            return (
              <div key={d.iso}>
                <h3 className="mb-3 font-mono-cinema text-[10px] uppercase tracking-[0.25em] text-gold">
                  Dag {d.day} · {d.label}
                </h3>
                {dayFilms.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border/60 p-4 text-sm text-foreground/50">
                    Nog geen film voor deze avond.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {dayFilms.map((f) => (
                      <div key={f.id} className="rounded-xl border border-border bg-night-deep/40 p-4">
                        <div className="flex flex-wrap items-start gap-4">
                          <PosterPreview pathOrUrl={f.poster_url} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-display text-lg">{f.title || "(Zonder titel)"}</span>
                              <span
                                className={`rounded-full border px-2 py-0.5 font-mono-cinema text-[9px] uppercase ${
                                  f.status === "published"
                                    ? "border-gold/50 text-gold"
                                    : f.status === "hidden"
                                      ? "border-foreground/30 text-foreground/60"
                                      : "border-border text-foreground/70"
                                }`}
                              >
                                {f.status}
                              </span>
                              {f.show_on_homepage && f.status === "published" && (
                                <span className="rounded-full border border-gold/30 px-2 py-0.5 font-mono-cinema text-[9px] uppercase text-gold/80">
                                  homepage
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-foreground/70">
                              {[f.genre, f.age_rating && `Kijkwijzer ${f.age_rating}`, f.start_time && `${f.start_time}`, f.duration_minutes && `${f.duration_minutes} min`]
                                .filter(Boolean)
                                .join(" · ") || "Geen details"}
                            </p>
                            {f.teaser && <p className="mt-1 text-sm italic text-foreground/60">&ldquo;{f.teaser}&rdquo;</p>}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <label className="flex items-center gap-2 text-xs text-foreground/70">
                              <Switch
                                checked={f.show_on_homepage}
                                onCheckedChange={(v) => updateFilm(f.id, { show_on_homepage: v })}
                              />
                              Homepage
                            </label>
                            <Select
                              value={f.status}
                              onValueChange={(v) => updateFilm(f.id, { status: v as FilmStatus })}
                            >
                              <SelectTrigger className="h-9 w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Concept</SelectItem>
                                <SelectItem value="published">Gepubliceerd</SelectItem>
                                <SelectItem value="hidden">Verborgen</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button variant="ghostLine" size="sm" onClick={() => setEditing(f)}>
                              <Pencil className="h-4 w-4" /> Bewerken
                            </Button>
                            <Button variant="ghostLine" size="sm" onClick={() => setActivitiesFor(f)}>
                              <Sparkles className="h-4 w-4" /> Voorprogramma
                            </Button>
                            <Button variant="ghostLine" size="sm" onClick={() => setPreviewing(f)}>
                              <Eye className="h-4 w-4" /> Voorbeeld
                            </Button>
                            <Button variant="ghostLine" size="sm" onClick={() => deleteFilm(f.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <FilmDialog
        open={creating}
        onOpenChange={(o) => !o && setCreating(false)}
        initial={EMPTY_FORM}
        onSaved={() => {
          setCreating(false);
          loadFilms();
        }}
      />
      {editing && (
        <FilmDialog
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          initial={editing}
          filmId={editing.id}
          onSaved={() => {
            setEditing(null);
            loadFilms();
          }}
        />
      )}

      {activitiesFor && (
        <FilmActivitiesDialog
          open={!!activitiesFor}
          onOpenChange={(o) => !o && setActivitiesFor(null)}
          filmId={activitiesFor.id}
          filmTitle={activitiesFor.title}
          badgeEnabled={activitiesFor.show_activities_badge}
          badgeLabel={activitiesFor.activities_badge_label}
          onBadgeChange={(patch) => {
            setActivitiesFor((prev) => (prev ? { ...prev, ...patch } : prev));
            updateFilm(activitiesFor.id, patch);
          }}
        />
      )}

      <FilmPreviewDialog
        open={!!previewing}
        onOpenChange={(o) => !o && setPreviewing(null)}
        film={
          previewing
            ? {
                title: previewing.title,
                play_date: previewing.play_date,
                festival_day: previewing.festival_day,
                teaser: previewing.teaser,
                description: previewing.description,
                poster_url: previewing.poster_url,
                start_time: previewing.start_time,
                duration_minutes: previewing.duration_minutes,
                genre: previewing.genre,
                age_rating: previewing.age_rating,
                badges: previewing.badges,
                cta_label: previewing.cta_label,
                cta_url: previewing.cta_url,
                trailer_url: previewing.trailer_url,
              }
            : null
        }
      />

      <Toaster position="top-center" />
    </div>
  );
}

type DialogProps = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: Omit<FilmRow, "id" | "festival_day"> & { id?: string };
  filmId?: string;
  onSaved: () => void;
};

function CustomBadgeManager({ badges }: { badges: CustomBadge[] }) {
  const [items, setItems] = useState<CustomBadge[]>(badges);
  const [draft, setDraft] = useState<CustomBadge>({
    label: "",
    bg: "#7c3aed",
    fg: "#ffffff",
    border: "#c4b5fd",
    glow: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(badges);
  }, [badges]);

  async function persist(next: CustomBadge[]) {
    setSaving(true);
    try {
      await saveCustomBadges(next);
      await loadCustomBadges(true);
      toast.success("Badges bijgewerkt");
    } catch (e: any) {
      toast.error(e?.message ?? "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function addBadge() {
    const label = draft.label.trim();
    if (!label) return toast.error("Geef de badge een naam");
    if (canonicalPreset(label)) return toast.error("Deze naam is al een preset");
    if (items.some((b) => b.label.trim().toLowerCase() === label.toLowerCase()))
      return toast.error("Deze badge bestaat al");
    const next = [...items, { ...draft, label }];
    setItems(next);
    await persist(next);
    setDraft({ label: "", bg: "#7c3aed", fg: "#ffffff", border: "#c4b5fd", glow: true });
  }

  async function removeBadge(label: string) {
    if (!confirm(`Badge "${label}" verwijderen? Films die deze badge gebruiken behouden de tekst maar verliezen de styling.`))
      return;
    const next = items.filter((b) => b.label !== label);
    setItems(next);
    await persist(next);
  }

  async function updateBadge(label: string, patch: Partial<CustomBadge>) {
    const next = items.map((b) => (b.label === label ? { ...b, ...patch } : b));
    setItems(next);
    await persist(next);
  }

  return (
    <div className="mb-8 space-y-5 rounded-2xl border border-gold/30 bg-night-deep/40 p-5">
      <div className="flex items-center gap-2">
        <Tags className="h-4 w-4 text-gold" />
        <p className="font-display text-lg">Aangepaste badges</p>
      </div>
      <p className="text-sm text-foreground/70">
        Maak eigen badges met eigen kleuren. Deze verschijnen tussen de presets in het film-formulier en op de site.
      </p>

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((c) => {
            const s = resolveBadgeStyle(c.label, items);
            return (
              <div key={c.label} className="grid gap-3 rounded-xl border border-border/60 bg-night-deep/40 p-3 md:grid-cols-[auto_1fr_auto] md:items-center">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider ${s.className}`}
                  style={s.style}
                >
                  {c.label}
                </span>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground/70">
                  <label className="flex items-center gap-1.5">
                    Vulling
                    <input type="color" value={c.bg} onChange={(e) => updateBadge(c.label, { bg: e.target.value })} className="h-7 w-9 cursor-pointer rounded border border-border" />
                  </label>
                  <label className="flex items-center gap-1.5">
                    Tekst
                    <input type="color" value={c.fg} onChange={(e) => updateBadge(c.label, { fg: e.target.value })} className="h-7 w-9 cursor-pointer rounded border border-border" />
                  </label>
                  <label className="flex items-center gap-1.5">
                    Rand
                    <input type="color" value={c.border} onChange={(e) => updateBadge(c.label, { border: e.target.value })} className="h-7 w-9 cursor-pointer rounded border border-border" />
                  </label>
                  <label className="flex items-center gap-2">
                    <Switch checked={!!c.glow} onCheckedChange={(v) => updateBadge(c.label, { glow: v })} />
                    Gloed
                  </label>
                </div>
                <Button variant="ghostLine" size="sm" onClick={() => removeBadge(c.label)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="rounded-xl border border-dashed border-border/60 p-3">
        <p className="mb-3 font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Nieuwe badge</p>
        <div className="grid gap-3 md:grid-cols-[1fr_auto_auto_auto_auto_auto]">
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            placeholder="Bijv. Familie, Cult, Q&A"
          />
          <label className="flex items-center gap-1.5 text-xs text-foreground/70">
            Vulling
            <input type="color" value={draft.bg} onChange={(e) => setDraft({ ...draft, bg: e.target.value })} className="h-9 w-10 cursor-pointer rounded border border-border" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-foreground/70">
            Tekst
            <input type="color" value={draft.fg} onChange={(e) => setDraft({ ...draft, fg: e.target.value })} className="h-9 w-10 cursor-pointer rounded border border-border" />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-foreground/70">
            Rand
            <input type="color" value={draft.border} onChange={(e) => setDraft({ ...draft, border: e.target.value })} className="h-9 w-10 cursor-pointer rounded border border-border" />
          </label>
          <label className="flex items-center gap-2 text-xs text-foreground/70">
            <Switch checked={!!draft.glow} onCheckedChange={(v) => setDraft({ ...draft, glow: v })} />
            Gloed
          </label>
          <Button variant="hero" size="sm" onClick={addBadge} disabled={saving}>
            <Plus className="h-4 w-4" /> Toevoegen
          </Button>
        </div>
        {draft.label && (
          <div className="mt-3">
            <p className="mb-1.5 text-xs text-foreground/60">Voorbeeld:</p>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider ${resolveBadgeStyle(draft.label, [draft]).className}`}
              style={resolveBadgeStyle(draft.label, [draft]).style}
            >
              {draft.label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function FilmDialog({ open, onOpenChange, initial, filmId, onSaved }: DialogProps) {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [badgesText, setBadgesText] = useState(initial.badges.join(", "));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const customBadges = useCustomBadges();

  useEffect(() => {
    if (!file) {
      setFileBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    setForm(initial);
    setBadgesText(initial.badges.join(", "));
    setFile(null);
  }, [initial, open]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Titel is verplicht");
    if (!form.play_date) return toast.error("Speeldatum is verplicht");
    setSaving(true);

    let posterPath = form.poster_url;
    if (file) {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `posters/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const up = await supabase.storage.from("film-posters").upload(path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (up.error) {
        setSaving(false);
        return toast.error("Upload mislukt: " + up.error.message);
      }
      posterPath = path;
    }

    const badges = badgesText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      title: form.title.trim(),
      play_date: form.play_date,
      teaser: form.teaser?.trim() || null,
      description: form.description?.trim() || null,
      poster_url: posterPath,
      start_time: form.start_time?.trim() || null,
      duration_minutes: form.duration_minutes || null,
      genre: form.genre?.trim() || null,
      age_rating: form.age_rating?.trim() || null,
      badges,
      cta_label: form.cta_label?.trim() || null,
      cta_url: form.cta_url?.trim() || null,
      status: form.status,
      show_on_homepage: form.show_on_homepage,
      sort_order: form.sort_order || 0,
      trailer_url: form.trailer_url?.trim() || null,
    };

    if (filmId) {
      const { error } = await (supabase as any).from("festival_films").update(payload).eq("id", filmId);
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Film bijgewerkt");
    } else {
      const { error } = await (supabase as any).from("festival_films").insert(payload);
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Film toegevoegd");
    }
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {filmId ? "Film bewerken" : "Nieuwe film"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Basis */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Basis</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Titel *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bijv. La La Land" required />
              </div>
              <div className="space-y-1.5">
                <Label>Speeldatum *</Label>
                <Select value={form.play_date} onValueChange={(v) => setForm({ ...form, play_date: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FESTIVAL_DATES.map((d) => (
                      <SelectItem key={d.iso} value={d.iso}>Dag {d.day} — {d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Starttijd</Label>
                <Input value={form.start_time ?? ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="21:00" />
              </div>
              <div className="space-y-1.5">
                <Label>Duur (minuten)</Label>
                <Input
                  type="number"
                  value={form.duration_minutes ?? ""}
                  onChange={(e) => setForm({ ...form, duration_minutes: e.target.value ? parseInt(e.target.value, 10) : null })}
                  placeholder="118"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Genre</Label>
                <Input value={form.genre ?? ""} onChange={(e) => setForm({ ...form, genre: e.target.value })} placeholder="Drama · Muziek" />
              </div>
              <div className="space-y-1.5">
                <Label>Kijkwijzer</Label>
                <Select value={form.age_rating ?? "AL"} onValueChange={(v) => setForm({ ...form, age_rating: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["AL", "6", "9", "12", "14", "16", "18"].map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Volgorde binnen dag</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
            </div>
          </section>

          {/* Content */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Content</h3>
            <div className="space-y-1.5">
              <Label>Teaser <span className="text-foreground/50">({(form.teaser ?? "").length}/140)</span></Label>
              <Input
                value={form.teaser ?? ""}
                onChange={(e) => setForm({ ...form, teaser: e.target.value.slice(0, 140) })}
                placeholder="Korte pakkende zin (max. 140 tekens)"
                maxLength={140}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Beschrijving</Label>
              <RichTextEditor
                value={form.description ?? ""}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Uitgebreide beschrijving of synopsis"
                minHeight={180}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Badges</Label>
              <BadgeEditor value={badgesText} onChange={setBadgesText} customBadges={customBadges} />
              <p className="text-xs text-foreground/60">
                Klik op een badge om deze aan of uit te zetten. Extra vrije badges kun je met komma&apos;s toevoegen.
              </p>
            </div>
          </section>

          {/* Media */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Poster</h3>
            <div className="flex items-start gap-4">
              <PosterPreview pathOrUrl={form.poster_url} />
              <div className="flex-1 space-y-2">
                <Label>Poster uploaden (vervangt huidige)</Label>
                <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <p className="text-xs text-foreground/60">Aanbevolen: 3:4 verhouding, minimaal 800 × 1067 px.</p>
                {form.poster_url && (
                  <Button type="button" variant="ghostLine" size="sm" onClick={() => setForm({ ...form, poster_url: null })}>
                    Poster verwijderen
                  </Button>
                )}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Call-to-action</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Knoptekst</Label>
                <Input value={form.cta_label ?? ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Koop tickets" />
              </div>
              <div className="space-y-1.5">
                <Label>Knoplink</Label>
                <Input value={form.cta_url ?? ""} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="https://…" />
              </div>
            </div>
          </section>

          {/* Trailer */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Trailer</h3>
            <div className="space-y-1.5">
              <Label>YouTube-URL of video-ID</Label>
              <Input
                value={form.trailer_url ?? ""}
                onChange={(e) => setForm({ ...form, trailer_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=…"
              />
              {form.trailer_url && !parseYouTubeId(form.trailer_url) ? (
                <p className="text-xs text-destructive">Onbekend YouTube-formaat. Gebruik een youtube.com/watch, youtu.be, /embed of /shorts URL.</p>
              ) : (
                <p className="text-xs text-foreground/60">
                  Verschijnt als knop “Bekijk trailer” op /programma. Video laadt pas na klik.
                </p>
              )}
            </div>
          </section>

          {/* Publicatie */}
          <section className="space-y-3">
            <h3 className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-gold">Publicatie</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as FilmStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Concept — niet zichtbaar</SelectItem>
                    <SelectItem value="published">Gepubliceerd — zichtbaar op site</SelectItem>
                    <SelectItem value="hidden">Verborgen — offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-end gap-2 pb-1 text-sm">
                <Switch
                  checked={form.show_on_homepage}
                  onCheckedChange={(v) => setForm({ ...form, show_on_homepage: v })}
                />
                Toon op homepage (indien gepubliceerd)
              </label>
            </div>
          </section>

          <DialogFooter>
            <Button type="button" variant="ghostLine" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="button" variant="ghostLine" onClick={() => setPreviewOpen(true)}>
              <Eye className="h-4 w-4" /> Voorbeeld
            </Button>
            <Button type="submit" variant="hero" disabled={saving}>
              <Save className="h-4 w-4" /> {saving ? "Bezig…" : "Opslaan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <FilmPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        film={{
          title: form.title,
          play_date: form.play_date,
          festival_day: null,
          teaser: form.teaser,
          description: form.description,
          poster_url: fileBlobUrl ?? form.poster_url,
          start_time: form.start_time,
          duration_minutes: form.duration_minutes,
          genre: form.genre,
          age_rating: form.age_rating,
          badges: badgesText.split(",").map((s) => s.trim()).filter(Boolean),
          cta_label: form.cta_label,
          cta_url: form.cta_url,
          trailer_url: form.trailer_url,
        }}
      />
    </Dialog>
  );
}
